import {
  createContext,
  useContext,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type Ref,
  type ReactNode,
} from 'react';
import { Link, useLocation } from 'react-router';
import { PageCrumbs } from './Breadcrumbs';
import { EmptyArt, Icon, IconBadge, type IconName } from './icons';

type ButtonVariant = 'primary' | 'secondary' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  small?: boolean;
  /** Zeigt „Bitte warten …“ und sperrt die Schaltfläche (verhindert doppeltes Absenden). */
  busy?: boolean;
  /** Symbol links vor dem Text (dekorativ, die Beschriftung bleibt der Text). */
  icon?: IconName;
  /**
   * Auf schmalen Bildschirmen (bis 640 px) nur das Symbol zeigen; der Text bleibt für Screenreader
   * erhalten (`sr-only`) und als Tooltip (`title`). Nur sinnvoll zusammen mit `icon`.
   */
  compactOnMobile?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

/** Inhalt eines Knopfes: Symbol (dekorativ) und Beschriftung; bei `compact` wird die Beschriftung mobil unsichtbar. */
function ButtonContent({
  icon,
  compact,
  children,
}: {
  icon: IconName | undefined;
  compact: boolean;
  children: ReactNode;
}) {
  if (icon === undefined) return <>{children}</>;
  return (
    <>
      <Icon name={icon} size={20} />
      <span className={compact ? 'btn__label btn__label--compact' : 'btn__label'}>{children}</span>
    </>
  );
}

function buttonClasses(
  variant: ButtonVariant,
  small: boolean,
  icon: IconName | undefined,
  compact: boolean,
  extra?: string,
): string {
  return [
    'btn',
    variant === 'primary' ? '' : `btn--${variant}`,
    small ? 'btn--small' : '',
    icon !== undefined && compact ? 'btn--compact' : '',
    extra ?? '',
  ]
    .filter(Boolean)
    .join(' ');
}

export function Button({
  variant = 'primary',
  small = false,
  busy = false,
  icon,
  compactOnMobile = false,
  children,
  disabled,
  type = 'button',
  className,
  title,
  ...rest
}: ButtonProps) {
  const classes = buttonClasses(variant, small, icon, compactOnMobile, className);
  const tooltip = title ?? (compactOnMobile && typeof children === 'string' ? children : undefined);
  return (
    <button
      {...rest}
      type={type}
      className={classes}
      title={tooltip}
      disabled={disabled === true || busy}
    >
      <ButtonContent icon={icon} compact={compactOnMobile}>
        {busy ? 'Bitte warten …' : children}
      </ButtonContent>
    </button>
  );
}

/** Verweis im Aussehen einer Schaltfläche (Hauptaktion als Knopf statt Textlink). */
export function LinkButton({
  to,
  variant = 'primary',
  small = false,
  icon,
  compactOnMobile = false,
  children,
}: {
  to: string;
  variant?: 'primary' | 'secondary';
  small?: boolean;
  icon?: IconName;
  compactOnMobile?: boolean;
  children: ReactNode;
}) {
  const classes = buttonClasses(variant, small, icon, compactOnMobile);
  return (
    <Link
      className={classes}
      to={to}
      title={compactOnMobile && typeof children === 'string' ? children : undefined}
    >
      <ButtonContent icon={icon} compact={compactOnMobile}>
        {children}
      </ButtonContent>
    </Link>
  );
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string | undefined;
}

/** Eingabefeld mit Beschriftung, Hinweis und Fehlertext (für Screenreader verknüpft). */
export function TextField({ label, hint, error, ...rest }: TextFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  // Eigene aria-Angaben des Aufrufers (z. B. Fehlertext der ganzen Zeile) bleiben erhalten und werden ergänzt
  const describedBy = [
    hint === undefined ? null : hintId,
    error === undefined ? null : errorId,
    rest['aria-describedby'] ?? null,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        {...rest}
        id={id}
        aria-invalid={error === undefined ? rest['aria-invalid'] : true}
        aria-describedby={describedBy === '' ? undefined : describedBy}
      />
      {hint !== undefined && (
        <span id={hintId} className="field__hint">
          {hint}
        </span>
      )}
      {error !== undefined && (
        <span id={errorId} className="field__error">
          {error}
        </span>
      )}
    </div>
  );
}

export function Checkbox({
  label,
  ...rest
}: { label: ReactNode } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'id'>) {
  const id = useId();
  return (
    <div className="check">
      <input {...rest} id={id} type="checkbox" />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}

/**
 * Speicherleiste unter einem Formular (Plan 16 U12): „Speichern“ ist erst mit einer Änderung bedienbar, der Stand steht
 * als Text daneben („Ungespeicherte Änderungen“). Auf dem Handy klebt die Leiste über der unteren Navigation am
 * Bildschirmrand, damit man nicht zum Seitenende scrollen muss. Gehört in ein `<form>`: Der Knopf sendet es ab.
 */
export function FormActions({
  dirty,
  busy = false,
  saveLabel = 'Speichern',
  onDiscard,
  note,
}: {
  /** Es gibt ungespeicherte Änderungen. */
  dirty: boolean;
  busy?: boolean;
  saveLabel?: string;
  /** Verwerfen der Eingaben (Knopf nur bei Änderungen). */
  onDiscard?: () => void;
  /** Ersetzt den Standardtext zum Stand. */
  note?: ReactNode;
}) {
  return (
    <div className="form-actions">
      <p className="form-actions__status" role="status">
        {note ?? (dirty ? 'Ungespeicherte Änderungen.' : 'Keine Änderungen.')}
      </p>
      <div className="form-actions__buttons">
        {onDiscard !== undefined && dirty && (
          <Button variant="secondary" disabled={busy} onClick={onDiscard}>
            Verwerfen
          </Button>
        )}
        <Button type="submit" busy={busy} disabled={!dirty}>
          {saveLabel}
        </Button>
      </div>
    </div>
  );
}

type AlertKind = 'error' | 'success' | 'info';

/** Fehler werden sofort vorgelesen (role=alert), Erfolg und Hinweise höflich (role=status). */
export function Alert({ kind, children }: { kind: AlertKind; children: ReactNode }) {
  return (
    <div className={`alert alert--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

/**
 * Feste Live-Region für Rückmeldungen („Als ausgezahlt vermerkt“): die Region bleibt immer im Dokument, damit
 * Screenreader auch die erste Meldung ansagen. `message === null` blendet die Meldung aus.
 */
export function ToastRegion({
  message,
  kind = 'success',
  onClose,
}: {
  message: string | null;
  kind?: 'success' | 'error';
  onClose?: () => void;
}) {
  return (
    <div className="toast-region" role={kind === 'error' ? 'alert' : 'status'} aria-live="polite">
      {message !== null && (
        <div className={kind === 'error' ? 'toast toast--error' : 'toast'}>
          {message}
          {onClose !== undefined && (
            <>
              {' '}
              <button type="button" className="btn btn--secondary btn--small" onClick={onClose}>
                Schließen
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Einheitlicher Leerzustand: kleine Illustration, kurzer Text und (optional) eine klare Aktion als Knopf.
 * `action` ist z. B. ein `LinkButton` oder `Button`; ohne Aktion bleibt es ein ruhiger Hinweis.
 */
export function EmptyState({
  title,
  children,
  action,
  art,
  headingLevel = 2,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  /** Eigene Illustration; Standard ist die offene Kiste. */
  art?: ReactNode;
  /** Ebene der Überschrift, passend zur Umgebung (Standard 2). */
  headingLevel?: 2 | 3 | 4;
}) {
  const Heading = `h${String(headingLevel)}` as 'h2' | 'h3' | 'h4';
  return (
    <div className="empty-card">
      {art ?? <EmptyArt />}
      <Heading className="empty-card__title">{title}</Heading>
      {children !== undefined && <p className="muted empty-card__text">{children}</p>}
      {action !== undefined && <div className="empty-card__actions">{action}</div>}
    </div>
  );
}

export function Card({ children }: { children: ReactNode }) {
  return <section className="card">{children}</section>;
}

/** Ebene der Kachel-Überschriften: unter einer Gruppen-Überschrift (h2) sind Kacheln h3, sonst h2. */
const TileLevel = createContext<2 | 3>(2);

/**
 * Eine Kachel zu einer Unterseite (Baustein 8, Plan Nr. 38): Titel plus kurze Kennzahl/Stand, optional mit
 * Symbol und Zähler. Gemeinsamer Baustein für alle Ebenen (Plattform, Organisation, Basar, Verkäufer, Profil).
 * `disabled` zeigt die Kachel ohne Link mit Begründung (z. B. „erst nach dem Veröffentlichen“), damit man
 * sieht, dass es das Thema gibt.
 */
export function Tile({
  to,
  title,
  description,
  status,
  icon,
  badge,
  disabled,
}: {
  to: string;
  title: string;
  /** Fester Zweck der Kachel (Satzfragment ohne Punkt), unabhängig vom aktuellen Stand. */
  description: ReactNode;
  /** Aktueller Stand, getrennt vom Zweck (z. B. „3 von 10 gedruckt“, „2 ungelesen“). */
  status?: ReactNode;
  icon?: IconName;
  badge?: ReactNode;
  /** Begründung, warum die Kachel gerade nicht nutzbar ist. */
  disabled?: string;
}) {
  const level = useContext(TileLevel);
  const Heading = level === 2 ? 'h2' : 'h3';
  const body = (
    <>
      {(icon !== undefined || badge !== undefined) && (
        <span className="tile__top">
          {icon !== undefined && <IconBadge name={icon} />}
          {badge !== undefined && <span className="badge tile__badge">{badge}</span>}
        </span>
      )}
      <Heading className="tile__title">{title}</Heading>
      <p className="muted tile__meta">{description}</p>
      {disabled !== undefined ? (
        <p className="tile__status">{disabled}</p>
      ) : (
        status !== undefined && <p className="tile__status">{status}</p>
      )}
    </>
  );
  if (disabled !== undefined) {
    return (
      <div className="card tile tile--disabled" aria-disabled="true">
        {body}
      </div>
    );
  }
  return (
    <Link className="card tile tile-link" to={to}>
      {body}
    </Link>
  );
}

/** Überschrift (optional) plus Kachel-Raster darunter (Baustein 8). */
export function TileGroup({ title, children }: { title?: string; children: ReactNode }) {
  const id = useId();
  if (title === undefined) return <div className="tiles">{children}</div>;
  return (
    <section className="stack" aria-labelledby={id}>
      <h2 id={id}>{title}</h2>
      <TileLevel.Provider value={3}>
        <div className="tiles">{children}</div>
      </TileLevel.Provider>
    </section>
  );
}

/**
 * Gemeinsamer Seitenkopf: Zurück-Link „‹ Elternseite“ (leiser Text-Link ohne Rahmen, immer sichtbar, Ziel ist `back.to`), am Desktop daneben
 * die Brotkrumen der Vorfahren (Start › Organisation › Basar, das Elternglied steht nur im Knopf), Titel mit Symbol und
 * Kontext-Chip (z. B. Rolle oder Status) und Aktionen rechts. `back` ist das direkte Elternglied der Seite, der Rest
 * ergibt sich aus Adresse und Kontext der Basar-Hülle (Baustein 8, Plan Nr. 18; Plan 16 U19b).
 */
export function PageHeader({
  back,
  title,
  titleId,
  meta,
  description,
  icon,
  chip,
  actions,
}: {
  back: {
    to: string;
    label: string;
    /** Seiten mit ungespeicherten Änderungen: `false` bricht den Wechsel ab (die Seite fragt selbst nach). */
    confirm?: () => boolean;
  };
  title: string;
  /** `id` der Überschrift, damit Gruppen auf der Seite sich per `aria-labelledby` darauf beziehen können. */
  titleId?: string;
  /** Zusatzzeile unter dem Titel (Termin, Art der Organisation …). */
  meta?: ReactNode;
  /** Eine freundliche Zeile unter dem Titel: wofür diese Seite da ist. */
  description?: ReactNode;
  /** Symbol vor dem Titel (dekorativ). */
  icon?: IconName;
  /** Kontext-Chip neben dem Titel, z. B. die eigene Rolle oder der Status. */
  chip?: ReactNode;
  /** Aktionen rechts neben dem Titel (Knöpfe, Links). */
  actions?: ReactNode;
}) {
  const location = useLocation();
  return (
    <header className="page-header">
      <div className="page-header__nav">
        <Link
          className="page-back"
          to={back.to}
          onClick={(click) => {
            if (back.confirm !== undefined && !back.confirm()) click.preventDefault();
          }}
        >
          <Icon name="chevron-left" size={18} />
          <span>{back.label}</span>
        </Link>
        <PageCrumbs back={back} current={title} pathname={location.pathname} />
      </div>
      <div className="page-header__row">
        <div className="page-header__title">
          {icon !== undefined && <IconBadge name={icon} />}
          <h1 id={titleId}>{title}</h1>
          {chip !== undefined && <span className="badge page-header__chip">{chip}</span>}
        </div>
        {actions !== undefined && <div className="page-header__actions">{actions}</div>}
      </div>
      {meta !== undefined && <p className="muted">{meta}</p>}
      {description !== undefined && <p className="page-description">{description}</p>}
    </header>
  );
}

export interface StatItem {
  label: string;
  value: ReactNode;
  /** Optionaler Zusatz unter dem Wert, z. B. „3 offen“. */
  hint?: ReactNode;
  tone?: 'ok' | 'warn';
}

/** Kennzahlen-Leiste (2 bis 4 Werte): Wert groß, Beschriftung klein, mobil zweispaltig. */
export function StatStrip({ items, label }: { items: StatItem[]; label?: string }) {
  return (
    <dl className="stat-strip stat-strip--cards" aria-label={label}>
      {items.map((item) => (
        <div
          key={item.label}
          className={
            item.tone === undefined
              ? 'stat-strip__item'
              : `stat-strip__item stat-strip__item--${item.tone}`
          }
        >
          <dd className="stat-strip__value">{item.value}</dd>
          <dt className="stat-strip__label">{item.label}</dt>
          {item.hint !== undefined && <dd className="stat-strip__hint">{item.hint}</dd>}
        </div>
      ))}
    </dl>
  );
}
